import { Box, Button, IconButton, Typography } from "@mui/material";
import { SettingsIcon, SearchIcon, DeleteIcon, EditIcon } from "../../../icons";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { deleteTemplate, editTemplate, getTemplates } from "../../../services/reportService";
import { formatDate } from "../../../utils/utils";
import { ContainedButtonStyles } from "../../../styles/GlobalStyles";
import { toast } from "react-toastify";
import { GridPreview } from "../TemplateModal";

const parseContents = (contents: unknown) => {
  try {
    let parsed = contents || "[]";
    // 이중 인코딩된 문자열("\"[...]\"") 대응
    while (typeof parsed === "string") parsed = JSON.parse(parsed);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// templates 테이블(사용자 저장 템플릿) 관리: 이름 수정·삭제는 즉시 반영, 클릭 시 구성 미리보기
export default function TemplateSettings() {
  const [searchTemplate, setSearchTemplate] = useState("");
  const [templateList, setTemplateList] = useState<templateItem[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<templateItem | null>(null);
  const [templateName, setTemplateName] = useState<string | null>(null);
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null); // editMode 대신 추가
  const [previewId, setPreviewId] = useState<number | null>(null);

  const fetchTemplates = async () => {
    try {
      const response = await getTemplates(searchTemplate || "");
      setTemplateList(response.templates);
    } catch (error) {
      setTemplateList([]);
      console.error(error);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [searchTemplate]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTemplate(value);
  };

  const handleEdit = (template: templateItem) => {
    setSelectedTemplate(template);
    setEditingTemplateId(template.id!);
    setTemplateName(template.name);
  };

  const handleDelete = async (template: templateItem) => {
    setSelectedTemplate(template);
    if (window.confirm("해당 템플릿을 삭제하시겠습니까? 삭제된 템플릿은 복구할 수 없습니다.")) {
      try {
        await deleteTemplate(template.id!);
        setTemplateList(templateList.filter((item) => item.id !== template.id));
      } catch (error: any) {
        toast.error(error.message);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // if (value.length <= 50) {
    //   setTemplateName(value.trim());
    // }
    setTemplateName(value);
  };

  const handleSave = async () => {
    try {
      if (editingTemplateId && templateName) {
        const response = await editTemplate(editingTemplateId, templateName);
        setTemplateList(
          templateList.map((template) =>
            template.id === editingTemplateId ? { ...template, name: templateName } : template,
          ),
        );
        setEditingTemplateId(null);
        setTemplateName(null);
        toast.success(response.message);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        overflow: "hidden",
      }}
    >
      {/* 섹션 헤더 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "18px",
          py: "14px",
          borderBottom: "1px solid #f3f4f6",
          background: "#fafafa",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: "6px",
              background: "#f3f4f6",
              border: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SettingsIcon sx={{ fontSize: 14, color: "#6b7280" }} />
          </Box>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{t("TemplateSettings")}</Typography>
          {templateList?.length > 0 && (
            <Box
              sx={{
                px: "7px",
                py: "1px",
                borderRadius: "20px",
                background: "#f3f4f6",
                border: "1px solid #e5e7eb",
              }}
            >
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#6b7280" }}>
                {templateList.length}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ p: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
        {/* 검색창 */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            px: "10px",
            height: "36px",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            background: "#f9fafb",
            transition: "all .15s",
            "&:focus-within": {
              border: "1px solid #93c5fd",
              background: "#fff",
            },
          }}
        >
          <SearchIcon sx={{ fontSize: 15, color: "#9ca3af", flexShrink: 0 }} />
          <input
            type="text"
            placeholder="템플릿 이름"
            value={searchTemplate}
            onChange={handleSearch}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              fontSize: "12px",
              color: "#374151",
              fontFamily: "inherit",
            }}
          />
        </Box>

        {/* 템플릿 목록 */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {templateList?.map((template) => (
            <Box
              key={template.id}
              sx={{
                borderRadius: "9px",
                border: "1px solid #e5e7eb",
                background: "#fafafa",
                overflow: "hidden",
                transition: "border-color .15s",
                "&:hover": { borderColor: "#d1d5db" },
              }}
            >
              {editingTemplateId === template.id && templateName !== null ? (
                /* 편집 모드 */
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    p: "10px 12px",
                  }}
                >
                  <input
                    type="text"
                    value={templateName}
                    onChange={handleChange}
                    style={{
                      flex: 1,
                      height: "34px",
                      padding: "0 12px",
                      border: "1px solid #93c5fd",
                      borderRadius: "7px",
                      outline: "none",
                      fontSize: "12px",
                      color: "#374151",
                      background: "#fff",
                      fontFamily: "inherit",
                    }}
                  />
                  <button
                    onClick={() => {
                      setSelectedTemplate(null);
                      setTemplateName(null);
                      setEditingTemplateId(null);
                    }}
                    style={{
                      padding: "5px 12px",
                      borderRadius: "7px",
                      border: "1px solid #e5e7eb",
                      background: "#fff",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#6b7280",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    onClick={handleSave}
                    style={{
                      padding: "5px 12px",
                      borderRadius: "7px",
                      border: "1px solid #bfdbfe",
                      background: "#eff6ff",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#3b82f6",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("Save")}
                  </button>
                </Box>
              ) : (
                /* 일반 모드 */
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: "14px",
                    py: "11px",
                  }}
                >
                  <Box
                    onClick={() => setPreviewId(previewId === template.id ? null : template.id!)}
                    sx={{ display: "flex", flexDirection: "column", gap: "2px", flex: 1, minWidth: 0, cursor: "pointer" }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#111827",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {template.name}
                    </Typography>
                    {template.description && (
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#6b7280",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {template.description}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: "10px", color: "#9ca3af" }}>
                      {formatDate(template.create_at)} · {parseContents(template.contents).length}개 항목 ·{" "}
                      {previewId === template.id ? "구성 닫기 ▲" : "구성 보기 ▼"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: "2px", flexShrink: 0, ml: "10px" }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(template)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "7px",
                        color: "#9ca3af",
                        "&:hover": { background: "#f3f4f6", color: "#374151" },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(template)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "7px",
                        color: "#9ca3af",
                        "&:hover": { background: "#fff5f5", color: "#ef4444" },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Box>
              )}
              {previewId === template.id && (
                <Box sx={{ px: "12px", pb: "12px" }}>
                  {parseContents(template.contents).length ? (
                    <GridPreview contents={parseContents(template.contents)} />
                  ) : (
                    <Typography sx={{ fontSize: "12px", color: "#9ca3af" }}>구성된 항목이 없습니다</Typography>
                  )}
                </Box>
              )}
            </Box>
          ))}

          {/* 빈 상태 */}
          {(!templateList || templateList.length === 0) && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: "24px",
                gap: "6px",
                opacity: 0.5,
              }}
            >
              <SearchIcon sx={{ fontSize: 24, color: "#9ca3af" }} />
              <Typography sx={{ fontSize: "12px", color: "#9ca3af" }}>템플릿이 없습니다</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
